import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import { createUsersApi } from '../api/usersApi';
import type { User } from '../userTypes';

interface UseGetUserResult {
  data: User | null;
  loading: boolean;
  error: ApiError | Error | null;
  refetch: () => void;
}

export function useGetUser(id: string): UseGetUserResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => createUsersApi(client), [client]);
  const { notifyError } = useNotification();

  const [data, setData] = useState<User | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setError(null);
        setData(await api.getById(id, { signal }));
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        const e = err as ApiError | Error;
        setError(e);
        if (ApiError.isApiError(e) && e.isServerError) notifyError(e);
        else if (!ApiError.isApiError(e)) notifyError(e);
      }
    },
    [api, id, notifyError],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- setState is called async (post-await), not synchronously
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  return { data, loading, error, refetch: () => void load() };
}
